/**
 * StudioPage — top-level entry.
 *
 * Handles auth gating (matching the ZinsserView pattern: verify the
 * Supabase user matches the persisted store, clear if not, then bootstrap
 * the entity caches). Renders StudioView once the auth + bootstrap
 * dance is done.
 */

import { useEffect, useRef, useState } from 'react'
import { useAuthStore } from '@/stores/useAuthStore'
import { useStudioStore } from '@/stores/useStudioStore'
import { StudioView } from './StudioView'

const STUDIO_USER_ID_KEY = 'studio-store-user-id'

export function StudioPage() {
  const authInitialized = useAuthStore((s) => s.initialized)
  const authUserId = useAuthStore((s) => s.user?.id)
  const initializeAuth = useAuthStore((s) => s.initialize)
  const bootstrap = useStudioStore((s) => s.bootstrap)
  const resetForUserSwitch = useStudioStore((s) => s.resetForUserSwitch)
  const lastError = useStudioStore((s) => s.lastError)

  const [isReady, setIsReady] = useState(false)
  const hasBootstrapped = useRef(false)

  useEffect(() => {
    initializeAuth()
  }, [initializeAuth])

  // User-isolation check.
  //
  // RequireAuth (wrapping this route) is what redirects unauthenticated
  // users to /login — we don't do it here. The only thing we care about
  // is the case where a different user previously logged in on this
  // browser and we're now seeing a new user_id; that's when we wipe the
  // persisted studio state so the previous user's view doesn't leak.
  //
  // We deliberately do NOT wipe when authUserId is briefly undefined
  // (Supabase nulls the session for a tick during token refresh).
  // Waiting for a real user_id before doing anything destructive avoids
  // the "all my data disappeared after a token refresh" failure mode.
  useEffect(() => {
    if (!authInitialized) return
    if (!authUserId) return  // no real user — wait, don't wipe

    const storedUserId = localStorage.getItem(STUDIO_USER_ID_KEY)

    if (storedUserId && storedUserId !== authUserId) {
      localStorage.setItem(STUDIO_USER_ID_KEY, authUserId)
      localStorage.removeItem('studio-store-v1')
      const persistApi = (useStudioStore as unknown as {
        persist?: { clearStorage: () => void; rehydrate: () => Promise<void> }
      }).persist
      persistApi?.clearStorage?.()
      resetForUserSwitch()
      persistApi?.rehydrate?.()
      hasBootstrapped.current = false
    } else if (!storedUserId) {
      localStorage.setItem(STUDIO_USER_ID_KEY, authUserId)
    }
    setIsReady(true)
  }, [authInitialized, authUserId, resetForUserSwitch])

  // Bootstrap caches once user-isolation is verified
  useEffect(() => {
    if (!isReady) return
    if (hasBootstrapped.current) return
    hasBootstrapped.current = true
    void bootstrap()
  }, [isReady, bootstrap])

  if (!isReady) {
    return (
      <div className="h-screen flex items-center justify-center bg-neutral-950 text-neutral-400 text-sm font-light">
        Loading studio…
      </div>
    )
  }

  return (
    <>
      <StudioView />
      {lastError && (
        <div className="fixed bottom-4 left-1/2 -translate-x-1/2 z-50 px-4 py-2 bg-red-950/90 border border-red-800/60 rounded text-xs text-red-200 font-light max-w-md">
          <div className="font-medium mb-0.5">Couldn't load studio data</div>
          <div className="opacity-80 break-words">{lastError}</div>
        </div>
      )}
    </>
  )
}
